/*
 * Avenue George V — comportements des sections gv-
 * Chargé une seule fois depuis layout/theme.liquid (defer).
 */

(() => {
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const slowConnection = () => {
    const c = navigator.connection;
    if (!c) return false;
    return c.saveData === true || /(^|-)2g$|^3g$/.test(c.effectiveType || '');
  };

  const setToggleState = (button, playing) => {
    if (!button) return;
    button.classList.toggle('is-paused', !playing);
    button.setAttribute('aria-label', playing ? button.dataset.labelPause : button.dataset.labelPlay);
  };

  /* ------------------------------------------------------------
   * Hero vidéo : monte la vidéo seulement si la connexion le permet
   * ------------------------------------------------------------ */
  class GvHeroVideo extends HTMLElement {
    connectedCallback() {
      if (!this.hasAttribute('data-gv-video') || this.video) return;
      if (slowConnection()) return;

      const template = this.querySelector('template[data-gv-video-template]');
      if (!template) return;

      const video = template.content.querySelector('video').cloneNode(true);
      video.muted = true;
      video.setAttribute('aria-hidden', 'true');
      video.setAttribute('tabindex', '-1');
      video.addEventListener('playing', () => this.classList.add('is-playing'), { once: true });
      this.querySelector('.gv-hero__media').appendChild(video);
      this.video = video;

      this.toggle = this.querySelector('[data-gv-video-toggle]');
      if (this.toggle) {
        this.toggle.hidden = false;
        this.toggle.addEventListener('click', () => {
          if (video.paused) video.play();
          else video.pause();
        });
        video.addEventListener('play', () => setToggleState(this.toggle, true));
        video.addEventListener('pause', () => setToggleState(this.toggle, false));
      }

      if (reducedMotion()) {
        video.removeAttribute('autoplay');
        video.pause();
        setToggleState(this.toggle, false);
        return;
      }

      const attempt = video.play();
      if (attempt && attempt.catch) attempt.catch(() => setToggleState(this.toggle, false));
    }
  }

  /* ------------------------------------------------------------
   * Vidéo créateur : lecture quand visible, pause sinon
   * ------------------------------------------------------------ */
  class GvInviewVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      if (!this.video) return;
      this.video.muted = true;
      this.toggle = this.querySelector('[data-gv-video-toggle]');
      this.userPaused = reducedMotion();
      setToggleState(this.toggle, false);

      this.video.addEventListener('play', () => setToggleState(this.toggle, true));
      this.video.addEventListener('pause', () => setToggleState(this.toggle, false));

      if (this.toggle) {
        this.toggle.addEventListener('click', () => {
          if (this.video.paused) {
            this.userPaused = false;
            this.video.play();
          } else {
            this.userPaused = true;
            this.video.pause();
          }
        });
      }

      if (slowConnection()) return;

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !this.userPaused) {
              const attempt = this.video.play();
              if (attempt && attempt.catch) attempt.catch(() => {});
            } else if (!entry.isIntersecting) {
              this.video.pause();
            }
          });
        },
        { threshold: 0.6 }
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }
  }

  /* ------------------------------------------------------------
   * Carrousel : flèches (desktop), état des boutons
   * ------------------------------------------------------------ */
  class GvCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.gv-carousel__track');
      if (!this.track) return;
      const root = this.closest('.gv-carousel') || this.parentElement;
      this.prev = root.querySelector('[data-gv-prev]');
      this.next = root.querySelector('[data-gv-next]');

      this.onScroll = () => this.update();
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });

      if (this.prev) this.prev.addEventListener('click', () => this.go(-1));
      if (this.next) this.next.addEventListener('click', () => this.go(1));
      this.update();
    }

    disconnectedCallback() {
      if (!this.track) return;
      this.track.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
    }

    go(direction) {
      const slide = this.track.querySelector('.gv-carousel__slide');
      const step = slide ? slide.getBoundingClientRect().width : this.track.clientWidth * 0.8;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const visible = Math.max(1, Math.floor(this.track.clientWidth / (step + gap)));
      this.track.scrollBy({
        left: direction * visible * (step + gap),
        behavior: reducedMotion() ? 'auto' : 'smooth',
      });
    }

    update() {
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
      if (this.next) this.next.disabled = this.track.scrollLeft >= max;
    }
  }

  /* ------------------------------------------------------------
   * Carte produit : pastilles couleur + ajout rapide
   * ------------------------------------------------------------ */
  class GvCard extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('script[data-gv-variants]');
      this.data = json ? JSON.parse(json.textContent) : null;
      this.color = this.data ? this.data.initialColor : null;
      this.image = this.querySelector('.gv-card__img--primary');
      this.status = this.querySelector('[data-gv-status]');

      this.querySelectorAll('[data-gv-color]').forEach((swatch) => {
        swatch.addEventListener('click', () => this.selectColor(swatch));
      });

      const toggle = this.querySelector('[data-gv-quick-toggle]');
      if (toggle) {
        toggle.addEventListener('click', () => {
          const open = toggle.getAttribute('aria-expanded') !== 'true';
          toggle.setAttribute('aria-expanded', String(open));
          this.classList.toggle('is-quick-open', open);
        });
      }

      this.querySelectorAll('[data-gv-size]').forEach((button) => {
        button.addEventListener('click', () => {
          const variant = this.findVariant(button.dataset.gvSize);
          if (variant) this.add(variant.id, button);
        });
      });

      const addDefault = this.querySelector('[data-gv-add-default]');
      if (addDefault) {
        addDefault.addEventListener('click', () => {
          const variant = this.findVariant(null);
          this.add(variant ? variant.id : this.data.defaultVariant, addDefault);
        });
      }

      this.syncSizes();
    }

    selectColor(swatch) {
      this.color = swatch.dataset.gvColor;
      this.querySelectorAll('[data-gv-color]').forEach((s) => {
        const active = s === swatch;
        s.classList.toggle('is-active', active);
        s.setAttribute('aria-pressed', String(active));
      });
      if (this.image && swatch.dataset.gvSrc) {
        this.image.srcset = swatch.dataset.gvSrcset || '';
        this.image.src = swatch.dataset.gvSrc;
      }
      this.syncSizes();
    }

    findVariant(size) {
      if (!this.data) return null;
      const { colorIndex, sizeIndex, variants } = this.data;
      return variants.find(
        (v) =>
          v.available &&
          (colorIndex === null || this.color === null || v.options[colorIndex] === this.color) &&
          (sizeIndex === null || size === null || v.options[sizeIndex] === size)
      );
    }

    syncSizes() {
      if (!this.data || this.data.sizeIndex === null) return;
      this.querySelectorAll('[data-gv-size]').forEach((button) => {
        const available = Boolean(this.findVariant(button.dataset.gvSize));
        button.disabled = !available;
      });
    }

    async add(variantId, trigger) {
      const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      const body = new FormData();
      body.append('id', variantId);
      body.append('quantity', 1);
      if (cart) {
        body.append('sections', cart.getSectionsToRender().map((section) => section.id));
        body.append('sections_url', window.location.pathname);
        cart.setActiveElement(trigger);
      }

      trigger.classList.add('is-loading');
      trigger.setAttribute('aria-disabled', 'true');

      try {
        const response = await fetch(`${window.routes.cart_add_url}`, {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body,
        });
        const state = await response.json();

        if (state.status) {
          this.announce(state.description || state.message || this.errorText());
          return;
        }
        if (!cart) {
          window.location = window.routes.cart_url;
          return;
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gv-card', productVariantId: variantId, cartData: state });
        }
        if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
        cart.renderContents(state);
        this.classList.remove('is-quick-open');
        const toggle = this.querySelector('[data-gv-quick-toggle]');
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
        this.announce(this.dataset.addedText || '');
      } catch (error) {
        console.error(error);
        this.announce(this.errorText());
      } finally {
        trigger.classList.remove('is-loading');
        trigger.removeAttribute('aria-disabled');
      }
    }

    errorText() {
      return this.dataset.errorText || '';
    }

    announce(message) {
      if (this.status && message) this.status.textContent = message;
    }
  }

  /* ------------------------------------------------------------
   * Collection : « Voir plus » / chargement au défilement
   * Sans JS, le bouton reste un lien vers la page suivante.
   * ------------------------------------------------------------ */
  class GvLoadMore extends HTMLElement {
    connectedCallback() {
      this.link = this.querySelector('[data-gv-load-more]');
      if (!this.link) return;

      this.link.addEventListener('click', (event) => {
        event.preventDefault();
        this.load(true);
      });

      if (this.dataset.mode === 'infinite' && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) this.load(false);
          },
          { rootMargin: '0px 0px 600px 0px' }
        );
        this.observer.observe(this);
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    async load(fromClick) {
      if (this.loading || !this.link) return;
      const grid = document.getElementById('product-grid');
      if (!grid) return;

      this.loading = true;
      this.link.classList.add('is-loading');
      this.link.setAttribute('aria-disabled', 'true');

      try {
        const url = new URL(this.link.href, window.location.origin);
        url.searchParams.set('section_id', this.dataset.sectionId);
        const response = await fetch(url.toString());
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');

        const items = html.querySelectorAll('#product-grid > li');
        const firstNew = items[0] ? document.importNode(items[0], true) : null;
        items.forEach((item, index) => grid.appendChild(index === 0 ? firstNew : document.importNode(item, true)));

        const next = html.querySelector('gv-load-more');
        const count = next && next.querySelector('[data-gv-count]');
        const bar = next && next.querySelector('[data-gv-bar]');
        if (count) this.querySelector('[data-gv-count]').innerHTML = count.innerHTML;
        if (bar) this.querySelector('[data-gv-bar]').setAttribute('style', bar.getAttribute('style'));

        const nextLink = next && next.querySelector('[data-gv-load-more]');
        if (nextLink) {
          this.link.href = nextLink.getAttribute('href');
        } else {
          this.link.remove();
          this.link = null;
          if (this.observer) this.observer.disconnect();
        }

        const live = this.querySelector('[data-gv-live]');
        if (live && this.dataset.loadedText) {
          live.textContent = this.dataset.loadedText.replace('[count]', items.length);
        }
        if (fromClick && firstNew) {
          const focusTarget = firstNew.querySelector('.gv-card__link');
          if (focusTarget) focusTarget.focus({ preventScroll: true });
        }
      } catch (error) {
        console.error(error);
        if (this.link) window.location.href = this.link.href;
      } finally {
        this.loading = false;
        if (this.link) {
          this.link.classList.remove('is-loading');
          this.link.removeAttribute('aria-disabled');
        }
        // Mode défilement : relance l'observation si la fin de liste est encore visible
        if (this.observer && this.link) {
          this.observer.unobserve(this);
          this.observer.observe(this);
        }
      }
    }
  }

  /* ------------------------------------------------------------
   * Page produit : guide des tailles à côté du libellé « Taille »
   * Dawn remplace le sélecteur de variantes à chaque changement :
   * le bouton est replacé automatiquement (MutationObserver).
   * ------------------------------------------------------------ */
  const SIZE_NAMES = ['taille', 'size', 'talla', 'pointure'];

  class GvSizeGuide extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-gv-size-guide-open]');
      this.dialog = this.querySelector('dialog');
      if (!this.button || !this.dialog) return;

      this.button.addEventListener('click', () => this.dialog.showModal());
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog || event.target.closest('[data-gv-dialog-close]')) this.dialog.close();
      });
      this.dialog.addEventListener('close', () => {
        if (this.button.isConnected) this.button.focus();
      });

      this.place();
      const root = document.getElementById(`ProductInfo-${this.dataset.sectionId}`);
      if (root) {
        this.observer = new MutationObserver(() => this.place());
        this.observer.observe(root, { childList: true, subtree: true });
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    place() {
      const selects = document.getElementById(`variant-selects-${this.dataset.sectionId}`);
      if (!selects) return;
      const legend = Array.from(selects.querySelectorAll('legend')).find((l) =>
        SIZE_NAMES.some((name) => l.textContent.trim().toLowerCase().startsWith(name))
      );
      if (!legend || this.button.parentElement === legend) return;
      legend.classList.add('gv-legend-with-guide');
      this.button.classList.add('is-inline');
      legend.appendChild(this.button);
    }
  }

  /* ------------------------------------------------------------
   * Page produit : date de livraison estimée (jours ouvrés)
   * Calculée côté navigateur pour ne pas être figée par le cache.
   * ------------------------------------------------------------ */
  class GvDeliveryEstimate extends HTMLElement {
    connectedCallback() {
      const min = parseInt(this.dataset.min, 10);
      const max = parseInt(this.dataset.max, 10);
      const cutoff = parseInt(this.dataset.cutoff, 10);
      if (!this.dataset.template || Number.isNaN(min) || Number.isNaN(max)) return;

      const isWeekend = (d) => d.getDay() === 0 || d.getDay() === 6;
      const addBusinessDays = (date, days) => {
        const d = new Date(date);
        let added = 0;
        while (added < days) {
          d.setDate(d.getDate() + 1);
          if (!isWeekend(d)) added += 1;
        }
        return d;
      };

      const now = new Date();
      let shipDay = new Date(now);
      if (isWeekend(now) || now.getHours() >= cutoff) shipDay = addBusinessDays(now, 1);

      let format;
      try {
        format = new Intl.DateTimeFormat(this.dataset.locale || undefined, {
          weekday: 'short',
          day: 'numeric',
          month: 'short',
        });
      } catch (error) {
        format = new Intl.DateTimeFormat(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
      }

      const start = format.format(addBusinessDays(shipDay, min));
      const end = format.format(addBusinessDays(shipDay, Math.max(min, max)));
      this.textContent = this.dataset.template.replace('[start]', start).replace('[end]', end);
    }
  }

  /* ------------------------------------------------------------
   * Page produit : bouton d'ajout au panier sticky (mobile)
   * ------------------------------------------------------------ */
  class GvStickyAtc extends HTMLElement {
    connectedCallback() {
      this.original = document.querySelector('main [id^="ProductSubmitButton-"]');
      if (!this.original) return;
      this.sectionId = this.original.id.replace('ProductSubmitButton-', '');
      this.button = this.querySelector('[data-gv-sticky-button]');
      this.priceEl = this.querySelector('[data-gv-sticky-price]');
      this.variantEl = this.querySelector('[data-gv-sticky-variant]');

      this.hidden = false;
      this.button.addEventListener('click', () => {
        if (this.original && !this.original.disabled) this.original.click();
      });

      this.observer = new IntersectionObserver(([entry]) => {
        const past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.classList.toggle('is-visible', past);
        document.body.classList.toggle('gv-sticky-atc-visible', past);
      });
      this.observer.observe(this.original);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (!event || !event.data || event.data.sectionId !== this.sectionId) return;
          // Laisse Dawn mettre à jour le prix et le bouton avant de les recopier
          requestAnimationFrame(() => this.sync(event.data.variant));
        });
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.unsubscribe) this.unsubscribe();
      document.body.classList.remove('gv-sticky-atc-visible');
    }

    sync(variant) {
      const original = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
      if (original) {
        this.original = original;
        this.observer.disconnect();
        this.observer.observe(original);
        this.button.disabled = original.disabled;
        const label = original.querySelector('span');
        if (label) this.button.textContent = label.textContent.trim();
      }

      const price = document.querySelector(`#price-${this.sectionId} .price`);
      if (price && this.priceEl) {
        const current = price.classList.contains('price--on-sale')
          ? price.querySelector('.price__sale .price-item--last')
          : price.querySelector('.price__regular .price-item--regular');
        if (current) this.priceEl.textContent = current.textContent.trim();
      }

      if (this.variantEl && variant && variant.title && variant.title !== 'Default Title') {
        this.variantEl.textContent = variant.title;
      }
    }
  }

  /* ------------------------------------------------------------
   * Page produit : « Me prévenir du retour » (produits en arrivage)
   * Visible quand la variante choisie est épuisée ; masque alors le bouton d'ajout (CSS).
   * ------------------------------------------------------------ */
  class GvBackInStock extends HTMLElement {
    connectedCallback() {
      if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        if (!event || !event.data || event.data.sectionId !== this.dataset.sectionId) return;
        const variant = event.data.variant;
        this.hidden = Boolean(variant && variant.available);
        const input = this.querySelector('[data-gv-notify-variant]');
        if (input && variant) input.value = variant.title;
      });
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
    }
  }

  /* Livraison offerte : conversion du seuil (exprimé en EUR) dans la devise du client.
     Shopify convertit la condition du tarif d'expédition avec le même taux (Shopify.currency.rate). */
  const gvCurrency = () => {
    const c = window.Shopify && window.Shopify.currency;
    const rate = c ? parseFloat(c.rate) : NaN;
    return c && rate > 0 ? { code: c.active, rate } : null;
  };
  const gvFormatMoney = (amount, code) => {
    try {
      return new Intl.NumberFormat((window.Shopify && window.Shopify.locale) || undefined, {
        style: 'currency',
        currency: code,
        minimumFractionDigits: 0,
        maximumFractionDigits: Number.isInteger(amount) ? 0 : 2,
      }).format(amount);
    } catch (e) {
      return `${amount} ${code}`;
    }
  };

  class GvCartProgress extends HTMLElement {
    connectedCallback() {
      if (this.dataset.currency === this.dataset.shopCurrency) return;
      const currency = gvCurrency();
      const threshold = parseInt(this.dataset.threshold, 10);
      const total = parseInt(this.dataset.total, 10);
      if (!currency || currency.code !== this.dataset.currency || Number.isNaN(threshold) || Number.isNaN(total)) return;

      // Arrondi à l'unité supérieure, comme les mentions du bandeau (ex. 300 € → 344 $US).
      const localThreshold = Math.ceil((threshold * currency.rate) / 100) * 100;
      const remaining = localThreshold - total;
      const progress = Math.min(100, (total * 100) / localThreshold);
      const text = this.querySelector('.gv-cart-progress__text');
      const bar = this.querySelector('.gv-cart-progress__bar');

      if (text) {
        text.innerHTML =
          remaining > 0
            ? this.dataset.templateRemaining.replace('__AMOUNT__', gvFormatMoney(Math.ceil(remaining) / 100, currency.code))
            : this.dataset.templateReached;
      }
      if (bar) {
        bar.setAttribute('aria-valuenow', Math.round(progress));
        bar.firstElementChild.style.setProperty('--gv-progress', `${progress}%`);
      }
      this.classList.toggle('is-complete', remaining <= 0);
      this.style.display = 'block';
    }
  }

  /* Mentions « 300 € » (bandeau d'annonce, onglet Livraison des fiches) dans la devise du client. */
  const gvConvertEuroMentions = () => {
    const currency = gvCurrency();
    if (!currency || currency.code === 'EUR') return;
    const pattern = /(\d+(?:[.,]\d+)?)\s?€/g;
    document
      .querySelectorAll('.announcement-bar__message, .product__accordion .accordion__content')
      .forEach((root) => {
        const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
        let node;
        while ((node = walker.nextNode())) {
          if (!pattern.test(node.nodeValue)) continue;
          pattern.lastIndex = 0;
          node.nodeValue = node.nodeValue.replace(pattern, (m, n) =>
            gvFormatMoney(Math.ceil(parseFloat(n.replace(',', '.')) * currency.rate), currency.code)
          );
        }
      });
  };
  gvConvertEuroMentions();

  const define = (name, element) => {
    if (!customElements.get(name)) customElements.define(name, element);
  };
  define('gv-cart-progress', GvCartProgress);
  define('gv-hero-video', GvHeroVideo);
  define('gv-inview-video', GvInviewVideo);
  define('gv-carousel', GvCarousel);
  define('gv-card', GvCard);
  define('gv-load-more', GvLoadMore);
  define('gv-size-guide', GvSizeGuide);
  define('gv-delivery-estimate', GvDeliveryEstimate);
  define('gv-sticky-atc', GvStickyAtc);
  define('gv-back-in-stock', GvBackInStock);
})();
